import { useState, useEffect } from 'react';
import { DataTable, Button, Modal } from '@sundkortid/ui';
import { PASS_LABELS_IS, formatIsk } from '@sundkortid/shared';
import type { PassType } from '@sundkortid/shared';
import { api } from '../../api/client';
import { AdminNav } from './AdminDashboardPage';

interface PricingConfig {
  id: string;
  poolId: string | null;
  poolName: string | null;
  passType: PassType;
  priceIsk: number;
  description: string | null;
  isActive: boolean;
  validFrom: string;
  validUntil: string | null;
  createdAt: string;
}

const EMPTY_FORM = { passType: 'single' as PassType, priceIsk: '', poolId: '', description: '' };

export function AdminPricingPage() {
  const [configs, setConfigs] = useState<PricingConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<PricingConfig | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchPricing = async () => {
    setLoading(true);
    try {
      const data = await api.get<PricingConfig[]>('/admin/pricing');
      setConfigs(data);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPricing(); }, []);

  const handleCreate = async () => {
    setActionLoading(true);
    try {
      await api.post('/admin/pricing', {
        passType: form.passType,
        priceIsk: parseInt(form.priceIsk, 10),
        poolId: form.poolId || undefined,
        description: form.description || undefined,
      });
      setShowCreate(false);
      setForm(EMPTY_FORM);
      fetchPricing();
    } catch {
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setActionLoading(true);
    try {
      await api.patch(`/admin/pricing/${deactivateTarget.id}`);
      setDeactivateTarget(null);
      fetchPricing();
    } catch {
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    {
      key: 'passType',
      header: 'Tegund',
      render: (row: PricingConfig) => PASS_LABELS_IS[row.passType],
    },
    {
      key: 'priceIsk',
      header: 'Verð',
      render: (row: PricingConfig) => formatIsk(row.priceIsk),
    },
    {
      key: 'description',
      header: 'Lýsing',
      render: (row: PricingConfig) => row.description ?? '—',
    },
    {
      key: 'poolName',
      header: 'Sundlaug',
      render: (row: PricingConfig) => row.poolName ?? 'Allar',
    },
    {
      key: 'isActive',
      header: 'Virk',
      render: (row: PricingConfig) => (
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${row.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
          {row.isActive ? 'Já' : 'Nei'}
        </span>
      ),
    },
    {
      key: 'validFrom',
      header: 'Gildir frá',
      render: (row: PricingConfig) => new Date(row.validFrom).toLocaleDateString('is-IS'),
    },
    {
      key: 'actions',
      header: 'Aðgerðir',
      render: (row: PricingConfig) =>
        row.isActive ? (
          <Button size="sm" variant="danger" onClick={() => setDeactivateTarget(row)}>
            Óvirkja
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100 px-6 py-4">
        <div className="max-w-6xl mx-auto flex flex-col gap-4">
          <h1 className="text-xl font-title font-bold text-primary">Sundkortið — Stjórnborð</h1>
          <AdminNav />
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-title font-bold text-primary">Verðlagning</h2>
          <Button onClick={() => { setForm(EMPTY_FORM); setShowCreate(true); }}>Nýtt verð</Button>
        </div>

        {loading ? (
          <p className="text-gray-500">Hleð gögnum...</p>
        ) : (
          <DataTable columns={columns} data={configs} keyExtractor={(row) => row.id} />
        )}
      </main>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Nýtt verð">
        <div className="space-y-3">
          <select
            value={form.passType}
            onChange={(e) => setForm({ ...form, passType: e.target.value as PassType })}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full"
          >
            <option value="single">Stakur miði</option>
            <option value="ten_pack">10 miða kort</option>
            <option value="monthly">Mánaðarkort</option>
            <option value="annual">Árskort</option>
          </select>
          <input
            type="number"
            placeholder="Verð (ISK)"
            value={form.priceIsk}
            onChange={(e) => setForm({ ...form, priceIsk: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full"
          />
          <input
            type="text"
            placeholder="Lýsing (t.d. Eitt skipti í þátttökusundlaug)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full"
          />
        </div>
        <div className="flex gap-2 justify-end mt-4">
          <Button variant="outline" onClick={() => setShowCreate(false)}>Hætta við</Button>
          <Button loading={actionLoading} onClick={handleCreate}>Búa til</Button>
        </div>
      </Modal>

      <Modal open={!!deactivateTarget} onClose={() => setDeactivateTarget(null)} title="Óvirkja verð">
        <p className="text-sm text-gray-600 mb-4">
          Ertu viss um að þú viljir óvirkja verð fyrir <strong>{PASS_LABELS_IS[deactivateTarget?.passType ?? 'single']}</strong> ({formatIsk(deactivateTarget?.priceIsk ?? 0)})?
        </p>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => setDeactivateTarget(null)}>Hætta við</Button>
          <Button variant="danger" loading={actionLoading} onClick={handleDeactivate}>Óvirkja</Button>
        </div>
      </Modal>
    </div>
  );
}
